import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BACKEND_URL } from '@/lib/backendUrl';
import { applySeo, resetSeoToDefault } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, Calendar, ChevronLeft, User, Tag,
  Share2, MessageCircle, Facebook, Twitter, Link2, ChevronDown,
} from 'lucide-react';

const SITE_ORIGIN = 'https://nfcesys.com.br';

const resolveImageUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) return `${BACKEND_URL}${u}`;
  return u;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const [postRes, listRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/blog/${slug}`),
          fetch(`${BACKEND_URL}/api/blog?limit=20`),
        ]);
        if (!postRes.ok) {
          setError(true);
          setLoading(false);
          return;
        }
        const postData = await postRes.json();
        setPost(postData);

        if (listRes.ok) {
          const listData = await listRes.json();
          setRelated(
            (listData.posts || [])
              .filter((p) => p.slug !== slug)
              .slice(0, 3)
          );
        }
      } catch (e) {
        console.error('Erro ao carregar artigo', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Scroll to top on slug change
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Resolve relative /api/uploads/... URLs inside the HTML content so generated/uploaded
  // images render properly from the backend.
  const renderedContent = useMemo(() => {
    if (!post?.content) return '';
    return post.content.replace(
      /(<img[^>]+src=["'])(\/api\/uploads\/[^"']+)/g,
      (_m, prefix, path) => `${prefix}${BACKEND_URL}${path}`
    );
  }, [post]);

  const shareUrl = post ? `${SITE_ORIGIN}/blog/${post.slug}` : '';
  const shareText = post ? `${post.title} — nfcesysPdv` : '';
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // simple visual cue without a toast lib here
      const btn = document.querySelector('[data-testid="share-copy"]');
      if (btn) {
        const orig = btn.getAttribute('aria-label');
        btn.setAttribute('aria-label', 'Link copiado!');
        setTimeout(() => btn.setAttribute('aria-label', orig || ''), 1500);
      }
    } catch {
      window.prompt('Copie o link:', shareUrl);
    }
  };

  useEffect(() => {
    if (!post) return;
    const canonical = `${SITE_ORIGIN}/blog/${post.slug}`;
    const absoluteCover = post.cover_image
      ? (post.cover_image.startsWith('http')
          ? post.cover_image
          : `${SITE_ORIGIN}${post.cover_image}`)
      : null;

    applySeo({
      title: `${post.title} | Blog nfcesysPdv`,
      description: post.meta_description || post.excerpt,
      keywords: post.keywords || [],
      canonical,
      type: 'article',
      image: absoluteCover || `${SITE_ORIGIN}/og-image.png`,
      imageAlt: post.title,
      publishedAt: post.published_at,
      updatedAt: post.updated_at,
      author: post.author,
      breadcrumbs: [
        { name: 'Home', url: `${SITE_ORIGIN}/` },
        { name: 'Blog', url: `${SITE_ORIGIN}/blog` },
        { name: post.title, url: canonical },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.meta_description || post.excerpt,
        author: { '@type': 'Organization', name: post.author },
        publisher: {
          '@type': 'Organization',
          name: 'nfcesysPdv',
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_ORIGIN}/logo.png`,
          },
        },
        datePublished: post.published_at,
        dateModified: post.updated_at,
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
        image: absoluteCover || `${SITE_ORIGIN}/og-image.png`,
        keywords: (post.keywords || []).join(', '),
        articleBody: (post.content || '').replace(/<[^>]+>/g, '').slice(0, 5000),
      },
      jsonLdId: 'article-jsonld',
    });

    // Inject FAQPage Schema separately so Google can render Rich Results.
    const existing = document.head.querySelector('script[data-seo-id="faq-jsonld"]');
    if (existing) existing.remove();
    if (Array.isArray(post.faqs) && post.faqs.length > 0) {
      const faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-seo-id', 'faq-jsonld');
      faqScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      });
      document.head.appendChild(faqScript);
    }

    return () => {
      resetSeoToDefault();
      const s = document.head.querySelector('script[data-seo-id="faq-jsonld"]');
      if (s) s.remove();
    };
  }, [post]);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300"
        data-testid="blog-post-loading"
      >
        Carregando artigo...
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300">
        <div className="text-center" data-testid="blog-post-not-found">
          <h1 className="text-2xl font-bold mb-3 text-white">Artigo não encontrado</h1>
          <p className="mb-6 text-slate-400">
            O conteúdo que você procura não existe ou foi removido.
          </p>
          <Link to="/blog">
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950">
              Ver todos os artigos
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100"
      data-testid="blog-post-page"
    >
      {/* Header */}
      <header className="border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/blog"
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="blog-post-back-list"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm">Todos os artigos</span>
          </Link>
          <button
            onClick={() => navigate('/')}
            className="text-xl font-bold tracking-tight"
            data-testid="blog-post-logo"
          >
            nfcesys<span className="text-amber-400">Pdv</span>
          </button>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/20 mb-5">
          Artigo
        </Badge>

        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 leading-tight"
          data-testid="blog-post-title"
        >
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-10 pb-8 border-b border-slate-800">
          <span className="inline-flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
          </span>
          <span className="inline-flex items-center gap-2">
            <User className="w-4 h-4" />
            {post.author}
          </span>
          {post.tags?.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 text-xs"
            >
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
        </div>

        {/* Cover image */}
        {post.cover_image && (
          <figure className="mb-10 -mx-2 sm:-mx-6 lg:-mx-12">
            <img
              src={resolveImageUrl(post.cover_image)}
              alt={post.title}
              loading="eager"
              className="w-full rounded-xl shadow-2xl shadow-black/40 max-h-[480px] object-cover"
              data-testid="blog-post-cover"
            />
          </figure>
        )}

        <div
          className="blog-content max-w-none"
          data-testid="blog-post-content"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />

        {/* FAQ section — visível + indexável (Schema injetado no <head>) */}
        {Array.isArray(post.faqs) && post.faqs.length > 0 && (
          <section className="mt-12" data-testid="blog-post-faqs">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Perguntas frequentes
            </h2>
            <div className="space-y-3">
              {post.faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group border border-slate-800 rounded-xl bg-slate-900/50 hover:border-slate-700 transition-colors"
                  data-testid={`blog-post-faq-${idx}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3 p-4 text-white font-semibold">
                    <span>{faq.question}</span>
                    <ChevronDown className="w-5 h-5 text-amber-400 transition-transform group-open:rotate-180 shrink-0" />
                  </summary>
                  <div className="px-4 pb-4 text-slate-300 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Share buttons */}
        <section className="mt-12 pt-6 border-t border-slate-800" data-testid="blog-post-share">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2 text-slate-400 text-sm font-medium">
              <Share2 className="w-4 h-4" />
              Compartilhar:
            </span>
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4" />
              Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium transition-colors"
              data-testid="share-twitter"
            >
              <Twitter className="w-4 h-4" />X (Twitter)
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition-colors"
              data-testid="share-copy"
              aria-label="Copiar link"
            >
              <Link2 className="w-4 h-4" />
              Copiar link
            </button>
          </div>
        </section>

        {/* CTA inside article */}
        <div className="mt-12 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
            Teste o nfcesysPdv grátis por 30 dias
          </h2>
          <p className="text-slate-300 mb-5">
            Sistema PDV completo com NFC-e, controle de estoque e WhatsApp Bot.
            Sem cartão, sem fidelidade.
          </p>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            onClick={() => navigate('/')}
            data-testid="blog-post-cta"
          >
            Começar agora
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <h2 className="text-2xl font-bold text-white mb-6">Continue lendo</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {related.map((rp) => (
              <Link
                key={rp.id}
                to={`/blog/${rp.slug}`}
                className="block rounded-xl border border-slate-800/80 bg-slate-900/50 hover:bg-slate-900 hover:border-amber-500/40 transition-all p-5"
                data-testid={`blog-related-${rp.slug}`}
              >
                <p className="text-xs text-slate-500 mb-2">
                  {formatDate(rp.published_at)}
                </p>
                <p className="text-white font-semibold leading-snug mb-2 line-clamp-3">
                  {rp.title}
                </p>
                <span className="inline-flex items-center gap-1 text-amber-400 text-sm">
                  Ler <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
